import React, { useEffect, useRef } from 'react';
import { View, StyleSheet, TouchableOpacity, Animated, Dimensions, Platform } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { useTutorial } from '../../context/TutorialContext';
import { useTheme } from '../../theme/ThemeContext';
import ThemedText from '../common/ThemedText';

const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = Dimensions.get('window');

// Icon mapping for each step
const getStepIcon = (targetTab: string): keyof typeof Ionicons.glyphMap => {
  switch (targetTab) {
    case 'Home':
      return 'home';
    case 'Work':
      return 'briefcase';
    case 'Clubs':
      return 'people';
    case 'Ledger':
      return 'wallet';
    case 'Network':
      return 'git-network';
    default:
      return 'information-circle';
  }
};

const TutorialOverlay: React.FC = () => {
  const { showTutorial, currentStep, totalSteps, currentStepData, nextStep, previousStep, skipTutorial } = useTutorial();
  const { theme } = useTheme();
  const insets = useSafeAreaInsets();
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const scaleAnim = useRef(new Animated.Value(0.9)).current;

  useEffect(() => {
    if (showTutorial) {
      Animated.parallel([
        Animated.timing(fadeAnim, {
          toValue: 1,
          duration: 300,
          useNativeDriver: true,
        }),
        Animated.spring(scaleAnim, {
          toValue: 1,
          friction: 8,
          tension: 40,
          useNativeDriver: true,
        }),
      ]).start();
    } else {
      fadeAnim.setValue(0);
      scaleAnim.setValue(0.9);
    }
  }, [showTutorial, currentStep]);

  if (!showTutorial || !currentStepData) {
    return null;
  }

  // Calculate tab bar position - tabs are at the bottom
  const tabBarHeight = 70 + insets.bottom;
  
  // Calculate which tab to highlight (left to right: Circles, Work, Home, Ledger, Network)
  const tabOrder = ['Clubs', 'Work', 'Home', 'Ledger', 'Network'];
  const tabIndex = tabOrder.indexOf(currentStepData.targetTab);
  const tabWidth = SCREEN_WIDTH / 5;
  const highlightX = tabIndex * tabWidth;

  return (
    <Animated.View 
      style={[
        styles.overlay,
        {
          opacity: fadeAnim,
        },
      ]}
    >
      {/* Dark overlay */}
      <View style={styles.darkOverlay} />

      {/* Highlight circle for the active tab */}
      <View
        style={[
          styles.highlightCircle,
          {
            bottom: insets.bottom + 2, // 2px from bottom (including safe area)
            left: highlightX + tabWidth / 2 - 32.5, // Center of tab minus half circle width (65/2)
          },
        ]}
      />

      {/* Tutorial card - centered vertically */}
      <Animated.View
        style={[
          styles.tutorialCard,
          {
            backgroundColor: theme.card,
            top: SCREEN_HEIGHT / 2 - 150, // Center vertically (approximate card height is 300px)
            transform: [{ scale: scaleAnim }],
          },
        ]}
      >
        {/* Skip button */}
        <TouchableOpacity
          style={styles.skipButton}
          onPress={skipTutorial}
          activeOpacity={0.7}
        >
          <ThemedText style={styles.skipText}>Skip</ThemedText>
        </TouchableOpacity>

        {/* Step indicator */}
        <View style={styles.stepIndicator}>
          {Array.from({ length: totalSteps }).map((_, index) => (
            <View
              key={index}
              style={[
                styles.stepDot,
                index === currentStep && styles.stepDotActive,
                { backgroundColor: index === currentStep ? '#A67D2E' : theme.border },
              ]}
            />
          ))}
        </View>

        {/* Content */}
        <View style={styles.content}>
          {/* Icon */}
          <View style={styles.iconContainer}>
            <Ionicons
              name={getStepIcon(currentStepData.targetTab)}
              size={48}
              color="#A67D2E"
            />
          </View>
          
          {/* Title */}
          <ThemedText style={styles.title}>{currentStepData.title}</ThemedText>
          
          {/* Description */}
          <ThemedText style={styles.description}>{currentStepData.description}</ThemedText>
        </View>

        {/* Navigation buttons */}
        <View style={styles.navigationButtons}>
          <TouchableOpacity
            style={[
              styles.navButton,
              currentStep === 0 && styles.navButtonDisabled,
            ]}
            onPress={previousStep}
            disabled={currentStep === 0}
            activeOpacity={0.7}
          >
            <Ionicons
              name="chevron-back"
              size={24}
              color={currentStep === 0 ? theme.textSecondary : '#A67D2E'}
            />
            <ThemedText
              style={[
                styles.navButtonText,
                currentStep === 0 && styles.navButtonTextDisabled,
              ]}
            >
              Back
            </ThemedText>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.navButton, styles.nextButton]}
            onPress={nextStep}
            activeOpacity={0.7}
          >
            <ThemedText style={[styles.navButtonText, styles.nextButtonText]}>
              {currentStep === totalSteps - 1 ? 'Get Started' : 'Next'}
            </ThemedText>
            <Ionicons
              name="chevron-forward"
              size={24}
              color="#FFFFFF"
            />
          </TouchableOpacity>
        </View>
      </Animated.View>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  overlay: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 9999,
    pointerEvents: 'box-none',
  },
  darkOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
  },
  highlightCircle: {
    position: 'absolute',
    width: 65,
    height: 65,
    borderRadius: 32.5,
    backgroundColor: 'rgba(166, 125, 46, 0.15)',
    borderWidth: 2.5,
    borderColor: '#A67D2E',
    shadowColor: '#A67D2E',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.6,
    shadowRadius: 12,
    elevation: 10,
  },
  tutorialCard: {
    position: 'absolute',
    left: 20,
    right: 20,
    borderRadius: 16,
    padding: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
  skipButton: {
    position: 'absolute',
    top: 16,
    right: 16,
    paddingHorizontal: 12,
    paddingVertical: 6,
    zIndex: 1,
  },
  skipText: {
    fontSize: 14,
    color: '#A67D2E',
    fontWeight: '600',
  },
  stepIndicator: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 20,
    gap: 8,
  },
  stepDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  stepDotActive: {
    width: 24,
    height: 8,
    borderRadius: 4,
  },
  content: {
    marginBottom: 24,
    alignItems: 'center',
  },
  iconContainer: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: 'rgba(166, 125, 46, 0.1)',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 16,
  },
  title: {
    fontSize: 26,
    fontWeight: '700',
    marginBottom: 16,
    textAlign: 'center',
    color: '#A67D2E',
  },
  description: {
    fontSize: 15,
    textAlign: 'left',
    lineHeight: 22,
    opacity: 0.85,
    paddingHorizontal: 8,
  },
  navigationButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  navButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
  },
  navButtonDisabled: {
    opacity: 0.3,
  },
  navButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#A67D2E',
  },
  navButtonTextDisabled: {
    opacity: 0.5,
  },
  nextButton: {
    backgroundColor: '#A67D2E',
  },
  nextButtonText: {
    color: '#FFFFFF',
  },
});

export default TutorialOverlay;
